import { fitFontSize } from '../../utils/textFit';
import React, { useState, useEffect, useRef } from 'react';
import { 
  Move, Upload, Save, Eye, Trash2, Plus, Settings2, 
  Image as ImageIcon, Type, QrCode, FileText, RotateCcw,
  ZoomIn, ZoomOut, Printer, Users, X, Check, Circle, Square
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/api/api';

// Standard I-Card Size (CR80): 85.6mm x 54mm
// For screen preview, we scale by ~3.78x
// Portrait orientation: 54mm width x 86mm height = 204px x 324px
const CARD_WIDTH = 204;  // 54mm scaled
const CARD_HEIGHT = 324; // 86mm scaled

// Sample data for preview
const SAMPLE_STUDENT = {
  name: 'Rahul Kumar Sharma',
  course: 'B.Tech Computer Science',
  student_no: 'STU2024001',
  session: '2024-2025',
  admission_date: '15/07/2024',
  blood_group: 'B+',
  phone: '9876543210',
  photo: '' // Will show placeholder
};

// Sample QR Code (base64 placeholder)
const SAMPLE_QR = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmZmYiLz48cmVjdCB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjQwIiB5PSIxMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iNzAiIHk9IjEwIiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSIxMCIgeT0iNDAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjMwIiB5PSI0MCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iNTAiIHk9IjQwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSI3MCIgeT0iNDAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjEwIiB5PSI3MCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iNDAiIHk9IjcwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSI2MCIgeT0iNzAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjgwIiB5PSI3MCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwIi8+PC9zdmc+';

// Available fields for I-Card
const AVAILABLE_FIELDS = {
  front: [
    { id: 'logo', label: 'College Logo', type: 'image', icon: ImageIcon },
    { id: 'college_name', label: 'College Name', type: 'text', icon: Type },
    { id: 'college_address', label: 'College Address', type: 'text', icon: Type },
    { id: 'college_contact', label: 'Email/Website/Phone', type: 'text', icon: Type },
    { id: 'photo', label: 'Student Photo', type: 'image', icon: ImageIcon },
    { id: 'name', label: 'Student Name', type: 'text', icon: Type },
    { id: 'course', label: 'Course', type: 'text', icon: Type },
    { id: 'student_no', label: 'Student No', type: 'text', icon: Type },
    { id: 'session', label: 'Session', type: 'text', icon: Type },
    { id: 'admission_date', label: 'Admission Date', type: 'text', icon: Type },
    { id: 'blood_group', label: 'Blood Group', type: 'text', icon: Type },
    { id: 'phone', label: 'Phone', type: 'text', icon: Type },
    { id: 'authority_signature', label: 'Authority Signature', type: 'signature', icon: ImageIcon },
    { id: 'authority_name', label: 'Authority Name', type: 'text', icon: Type },
    { id: 'authority_designation', label: 'Authority Designation', type: 'text', icon: Type },
  ],
  back: [
    { id: 'terms_title', label: 'Terms Title', type: 'text', icon: Type },
    { id: 'terms', label: 'Terms & Conditions', type: 'textarea', icon: FileText },
    { id: 'student_no_back', label: 'Student No', type: 'text', icon: Type },
    { id: 'qr_code', label: 'QR Code', type: 'qr', icon: QrCode },
    { id: 'authority_signature_back', label: 'Authority Signature', type: 'signature', icon: ImageIcon },
    { id: 'authority_name_back', label: 'Authority Name', type: 'text', icon: Type },
  ]
};

// Draggable Field Component
const DraggableField = ({ field, onUpdate, onDelete, selected, onSelect, scale }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fieldRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) {
      setIsResizing(true);
    } else {
      setIsDragging(true);
    }
    setDragStart({ x: e.clientX, y: e.clientY });
    onSelect(field.id);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging && !isResizing) return;
    
    const dx = (e.clientX - dragStart.x) / scale;
    const dy = (e.clientY - dragStart.y) / scale;
    
    if (isDragging) {
      onUpdate(field.id, {
        x: Math.max(0, Math.min(field.x + dx, 210 - field.width)),
        y: Math.max(0, Math.min(field.y + dy, 297 - field.height))
      });
    } else if (isResizing) {
      onUpdate(field.id, {
        width: Math.max(20, field.width + dx),
        height: Math.max(15, field.height + dy)
      });
    }
    
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart]);

  if (!field.visible) return null;

  const isRound = field.shape === 'round';

  const getFieldContent = () => {
    switch (field.type) {
      case 'image':
        return (
          <div 
            className={`w-full h-full bg-gray-200 border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-500 text-xs ${isRound ? 'rounded-full' : ''}`}
            style={{ overflow: 'hidden' }}
          >
            {field.id === 'logo' ? '🏛️' : '📷'}
          </div>
        );
      case 'signature':
        return (
          <div 
            className="w-full h-full bg-gray-100 border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-500 text-xs"
            style={{ overflow: 'hidden' }}
          >
            ✍️ Signature
          </div>
        );
      case 'qr':
        return (
          <div className="w-full h-full bg-white border border-gray-300 flex items-center justify-center">
            <QrCode className="w-3/4 h-3/4 text-gray-600" />
          </div>
        );
      case 'textarea':
        return (
          <div 
            className="w-full h-full overflow-hidden text-left p-1 bg-gray-50 border border-gray-200" 
            style={{ fontSize: `${field.fontSize}px`, color: field.color, lineHeight: '1.4' }}
          >
            <div className="opacity-60" style={{ fontSize: `${field.fontSize}px` }}>
              1. This card is non-transferable...<br/>
              2. Report loss immediately...<br/>
              3. This card must be carried...<br/>
              4. Misuse will result in...<br/>
              ...
            </div>
          </div>
        );
      default:
        return (
          <div 
            className="w-full h-full flex items-center overflow-hidden px-1 bg-blue-50/50 border border-blue-200/50"
            style={{ 
              fontSize: `${field.fontSize}px`, 
              color: field.color,
              fontWeight: field.fontWeight || 'normal',
              justifyContent: field.textAlign === 'center' ? 'center' : field.textAlign === 'right' ? 'flex-end' : 'flex-start'
            }}
          >
            {field.label}
          </div>
        );
    }
  };

  return (
    <div
      ref={fieldRef}
      className={`absolute cursor-move select-none ${selected ? 'ring-2 ring-blue-500 z-20' : 'z-10'}`}
      style={{
        left: `${field.x}px`,
        top: `${field.y}px`,
        width: `${field.width}px`,
        height: `${field.height}px`,
      }}
      onMouseDown={handleMouseDown}
      data-testid={`field-${field.id}`}
    >
      {getFieldContent()}
      
      {selected && (
        <>
          {/* Delete button */}
          <button
            className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 z-30"
            onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
          >
            <X className="w-3 h-3" />
          </button>
          
          {/* Resize handle */}
          <div 
            className="resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 cursor-se-resize rounded-sm z-30"
          />
        </>
      )}
    </div>
  );
};

// Field Properties Panel
const PropertiesPanel = ({ field, onUpdate }) => {
  if (!field) {
    return (
      <div className="p-4 text-gray-500 text-sm text-center">
        Select a field to edit properties
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h3 className="font-semibold text-gray-800 border-b pb-2">{field.label}</h3>
      
      {/* Shape option for images */}
      {field.type === 'image' && (
        <div>
          <label className="block text-xs text-gray-600 mb-2">Shape</label>
          <div className="flex gap-2">
            <button
              onClick={() => onUpdate(field.id, { shape: 'square' })}
              className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded border text-sm ${
                field.shape !== 'round' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600'
              }`}
            >
              <Square className="w-4 h-4" />
              Square
            </button>
            <button
              onClick={() => onUpdate(field.id, { shape: 'round' })}
              className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded border text-sm ${
                field.shape === 'round' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600'
              }`}
            >
              <Circle className="w-4 h-4" />
              Round
            </button>
          </div>
        </div>
      )}
      
      {(field.type === 'text' || field.type === 'textarea') && (
        <>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Size</label>
            <input
              type="range"
              min="6"
              max="24"
              value={field.fontSize || 10}
              onChange={(e) => onUpdate(field.id, { fontSize: parseInt(e.target.value) })}
              className="w-full"
            />
            <span className="text-xs text-gray-500">{field.fontSize || 10}px</span>
          </div>
          
          <div>
            <label className="block text-xs text-gray-600 mb-1">Color</label>
            <input
              type="color"
              value={field.color || '#000000'}
              onChange={(e) => onUpdate(field.id, { color: e.target.value })}
              className="w-full h-8 rounded cursor-pointer"
            />
          </div>
          
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Weight</label>
            <select
              value={field.fontWeight || 'normal'}
              onChange={(e) => onUpdate(field.id, { fontWeight: e.target.value })}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs text-gray-600 mb-1">Alignment</label>
            <select
              value={field.textAlign || 'left'}
              onChange={(e) => onUpdate(field.id, { textAlign: e.target.value })}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </>
      )}
      
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-600 mb-1">X</label>
          <input
            type="number"
            value={Math.round(field.x)}
            onChange={(e) => onUpdate(field.id, { x: parseInt(e.target.value) || 0 })}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Y</label>
          <input
            type="number"
            value={Math.round(field.y)}
            onChange={(e) => onUpdate(field.id, { y: parseInt(e.target.value) || 0 })}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Width</label>
          <input
            type="number"
            value={Math.round(field.width)}
            onChange={(e) => onUpdate(field.id, { width: parseInt(e.target.value) || 20 })}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Height</label>
          <input
            type="number"
            value={Math.round(field.height)}
            onChange={(e) => onUpdate(field.id, { height: parseInt(e.target.value) || 15 })}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
      </div>
      
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={field.visible}
            onChange={(e) => onUpdate(field.id, { visible: e.target.checked })}
            className="rounded"
          />
          <span className="text-sm">Visible</span>
        </label>
      </div>
    </div>
  );
};

// Main I-Card Builder Component
const ICardBuilder = () => {
  const [template, setTemplate] = useState({
    front_background: '',
    back_background: '',
    front_fields: [],
    back_fields: [],
    terms_text: '',
    terms_title: 'TERMS & CONDITIONS',
    college_name: '',
    college_address: '',
    college_email: '',
    college_website: '',
    college_phone: '',
    college_logo: '',
    authority_signature: '',
    authority_name: '',
    authority_designation: '',
  });
  const [activeSide, setActiveSide] = useState('front');
  const [selectedField, setSelectedField] = useState(null);
  const [scale, setScale] = useState(1.5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const response = await api.get('/icard-builder/template');
      setTemplate(response.data);
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };


  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/icard-builder/template', template);
      toast.success('Template saved successfully!');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleBackgroundUpload = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/icard-builder/upload-background?side=${side}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setTemplate(prev => ({
        ...prev,
        [`${side}_background`]: response.data.image_url
      }));
      toast.success('Background uploaded!');
    } catch (error) {
      toast.error('Failed to upload background');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/icard-builder/upload-background?side=logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setTemplate(prev => ({
        ...prev,
        college_logo: response.data.image_url
      }));
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 100KB)
    if (file.size > 100 * 1024) {
      toast.error('Signature image must be less than 100KB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setTemplate(prev => ({
        ...prev,
        authority_signature: reader.result
      }));
      toast.success('Signature uploaded!');
    };
    reader.onerror = () => {
      toast.error('Failed to read signature file');
    };
    reader.readAsDataURL(file);
  };

  const updateField = (fieldId, updates) => {
    const fieldKey = activeSide === 'front' ? 'front_fields' : 'back_fields';
    setTemplate(prev => ({
      ...prev,
      [fieldKey]: prev[fieldKey].map(f => 
        f.id === fieldId ? { ...f, ...updates } : f
      )
    }));
  };

  const deleteField = (fieldId) => {
    const fieldKey = activeSide === 'front' ? 'front_fields' : 'back_fields';
    setTemplate(prev => ({
      ...prev,
      [fieldKey]: prev[fieldKey].map(f => 
        f.id === fieldId ? { ...f, visible: false } : f
      )
    }));
    setSelectedField(null);
  };

  const addField = (fieldDef) => {
    const fieldKey = activeSide === 'front' ? 'front_fields' : 'back_fields';
    const existingField = template[fieldKey].find(f => f.id === fieldDef.id);
    
    if (existingField) {
      setTemplate(prev => ({
        ...prev,
        [fieldKey]: prev[fieldKey].map(f => 
          f.id === fieldDef.id ? { ...f, visible: true } : f
        )
      }));
    } else {
      const newField = {
        id: fieldDef.id,
        label: fieldDef.label,
        type: fieldDef.type,
        x: 50,
        y: 50,
        width: fieldDef.type === 'image' ? 60 : fieldDef.type === 'signature' ? 50 : fieldDef.type === 'qr' ? 50 : fieldDef.type === 'textarea' ? 180 : 100,
        height: fieldDef.type === 'image' ? 60 : fieldDef.type === 'signature' ? 25 : fieldDef.type === 'qr' ? 50 : fieldDef.type === 'textarea' ? 150 : 18,
        fontSize: 10,
        color: '#000000',
        fontWeight: ['name', 'college_name', 'terms_title'].includes(fieldDef.id) ? 'bold' : 'normal',
        textAlign: ['name', 'college_name', 'college_address', 'college_contact', 'course', 'student_no', 'terms_title', 'authority_name', 'authority_designation'].includes(fieldDef.id) ? 'center' : 'left',
        visible: true,
        shape: fieldDef.id === 'logo' ? 'round' : 'square'
      };
      
      setTemplate(prev => ({
        ...prev,
        [fieldKey]: [...prev[fieldKey], newField]
      }));
    }
  };

  const resetToDefault = async () => {
    if (!confirm('Reset template to default? All changes will be lost.')) return;
    setLoading(true);
    try {
      await api.post('/icard-builder/template', {
        front_background: '',
        back_background: '',
        front_fields: [],
        back_fields: [],
        terms_text: '',
        terms_title: 'TERMS & CONDITIONS',
        college_name: '',
        college_address: '',
        college_email: '',
        college_website: '',
        college_phone: '',
        college_logo: '',
      });
      await fetchTemplate();
      toast.success('Template reset to default');
    } catch (error) {
      toast.error('Failed to reset template');
    } finally {
      setLoading(false);
    }
  };

  const currentFields = activeSide === 'front' ? template.front_fields : template.back_fields;
  const currentBackground = activeSide === 'front' ? template.front_background : template.back_background;
  const availableFields = AVAILABLE_FIELDS[activeSide];
  const selectedFieldData = currentFields.find(f => f.id === selectedField);

  // Get contact info string
  const getContactInfo = () => {
    const parts = [];
    if (template.college_email) parts.push(template.college_email);
    if (template.college_website) parts.push(template.college_website);
    if (template.college_phone) parts.push(template.college_phone);
    return parts.join(' | ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Settings2 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">I-Card Template Builder</h1>
            <p className="text-sm text-gray-500">Design your I-Card using Drag & Drop</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={resetToDefault}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            data-testid="reset-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
          
          <button
            onClick={() => setShowPreviewModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            data-testid="preview-btn"
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            data-testid="save-btn"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Template
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Field Palette & College Info */}
        <div className="w-72 bg-white border-r overflow-y-auto">
          {/* College Information */}
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                🏛️ College Information
              </h3>
              <button
                onClick={async () => {
                  try {
                    const response = await api.get('/icard-builder/college-info');
                    setTemplate(prev => ({
                      ...prev,
                      college_name: response.data.college_name || prev.college_name,
                      college_address: response.data.college_address || prev.college_address,
                      college_email: response.data.college_email || prev.college_email,
                      college_website: response.data.college_website || prev.college_website,
                      college_phone: response.data.college_phone || prev.college_phone,
                      college_logo: response.data.college_logo || prev.college_logo,
                    }));
                    toast.success('College info loaded from system!');
                  } catch (error) {
                    toast.error('Failed to fetch college info');
                  }
                }}
                className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"
                title="Sync College info from System"
              >
                <RotateCcw className="h-3 w-3" />
                Sync
              </button>
            </div>
            
            {/* Logo Upload */}
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">College Logo</label>
              <label className="block">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center cursor-pointer hover:border-blue-400 transition-colors">
                  {template.college_logo ? (
                    <div className="flex items-center gap-2">
                      <img src={template.college_logo} alt="" className="h-10 w-10 rounded-full object-cover" />
                      <span className="text-xs text-gray-500">Click to change</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 justify-center">
                      <Upload className="h-4 w-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Upload Logo</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
              </label>
            </div>
            
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">College Name</label>
                <input
                  type="text"
                  value={template.college_name}
                  onChange={(e) => setTemplate(prev => ({ ...prev, college_name: e.target.value }))}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  placeholder="ABC College of Engineering"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Address</label>
                <input
                  type="text"
                  value={template.college_address}
                  onChange={(e) => setTemplate(prev => ({ ...prev, college_address: e.target.value }))}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  placeholder="123, Main Road, City - 700001"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Email</label>
                  <input
                    type="text"
                    value={template.college_email}
                    onChange={(e) => setTemplate(prev => ({ ...prev, college_email: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="info@college.edu"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Phone</label>
                  <input
                    type="text"
                    value={template.college_phone}
                    onChange={(e) => setTemplate(prev => ({ ...prev, college_phone: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="033-12345678"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Website</label>
                <input
                  type="text"
                  value={template.college_website}
                  onChange={(e) => setTemplate(prev => ({ ...prev, college_website: e.target.value }))}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  placeholder="www.college.edu"
                />
              </div>
            </div>
            
            {/* Authority Signature Section */}
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">✍️ Authority Signature</h4>
              
              {/* Signature Upload */}
              <div className="mb-3">
                <label className="block text-xs text-gray-600 mb-1">Signature Image (max 100KB)</label>
                <label className="block">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center cursor-pointer hover:border-blue-400 transition-colors">
                    {template.authority_signature ? (
                      <div className="flex items-center gap-2">
                        <img src={template.authority_signature} alt="" className="h-8 object-contain" />
                        <span className="text-xs text-gray-500">Click to change</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-center">
                        <Upload className="h-4 w-4 text-gray-400" />
                        <span className="text-xs text-gray-500">Upload Signature</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureUpload}
                      className="hidden"
                    />
                  </div>
                </label>
              </div>
              
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Authority Name</label>
                  <input
                    type="text"
                    value={template.authority_name}
                    onChange={(e) => setTemplate(prev => ({ ...prev, authority_name: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="Dr. John Smith"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Designation</label>
                  <input
                    type="text"
                    value={template.authority_designation}
                    onChange={(e) => setTemplate(prev => ({ ...prev, authority_designation: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="Principal"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Available Fields */}
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-800 mb-3">📌 Available Fields</h3>
            <div className="space-y-1.5">
              {availableFields.map(field => {
                const isAdded = currentFields.some(f => f.id === field.id && f.visible);
                return (
                  <button
                    key={field.id}
                    onClick={() => addField(field)}
                    disabled={isAdded}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                      isAdded 
                        ? 'bg-green-50 text-green-600 cursor-not-allowed' 
                        : 'bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700'
                    }`}
                    data-testid={`add-field-${field.id}`}
                  >
                    <field.icon className="h-4 w-4" />
                    {field.label}
                    {isAdded && <Check className="h-3 w-3 ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Background Upload */}
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-800 mb-3">🖼️ Background Image</h3>
            <label className="block">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 transition-colors">
                {currentBackground ? (
                  <div className="space-y-2">
                    <img src={currentBackground} alt="" className="h-16 mx-auto object-contain rounded" />
                    <p className="text-xs text-gray-500">Click to change</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-6 w-6 mx-auto text-gray-400 mb-1" />
                    <p className="text-xs text-gray-500">Upload {activeSide} background</p>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleBackgroundUpload(e, activeSide)}
                  className="hidden"
                />
              </div>
            </label>
            {currentBackground && (
              <button
                onClick={() => setTemplate(prev => ({ ...prev, [`${activeSide}_background`]: '' }))}
                className="w-full mt-2 text-xs text-red-600 hover:text-red-800"
              >
                Remove Background
              </button>
            )}
          </div>
          
          {/* Terms Text (Back side only) */}
          {activeSide === 'back' && (
            <div className="p-4">
              <h3 className="font-semibold text-gray-800 mb-3">📜 Terms & Conditions</h3>
              <div className="mb-2">
                <label className="block text-xs text-gray-600 mb-1">Title</label>
                <input
                  type="text"
                  value={template.terms_title}
                  onChange={(e) => setTemplate(prev => ({ ...prev, terms_title: e.target.value }))}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  placeholder="TERMS & CONDITIONS"
                />
              </div>
              <textarea
                value={template.terms_text}
                onChange={(e) => setTemplate(prev => ({ ...prev, terms_text: e.target.value }))}
                rows={8}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                placeholder="Enter terms and conditions (one per line)..."
              />
            </div>
          )}
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Side Toggle & Zoom */}
          <div className="bg-white border-b px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setActiveSide('front'); setSelectedField(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSide === 'front' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="front-side-btn"
              >
                Front Side
              </button>
              <button
                onClick={() => { setActiveSide('back'); setSelectedField(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSide === 'back' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="back-side-btn"
              >
                Back Side
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
                className="p-1.5 hover:bg-gray-100 rounded"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600 w-14 text-center">{Math.round(scale * 100)}%</span>
              <button
                onClick={() => setScale(s => Math.min(3, s + 0.25))}
                className="p-1.5 hover:bg-gray-100 rounded"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {/* Canvas Area */}
          <div 
            className="flex-1 overflow-auto p-8 flex items-start justify-center"
            onClick={() => setSelectedField(null)}
          >
            <div
              ref={canvasRef}
              className="relative bg-white shadow-2xl"
              style={{
                width: `${CARD_WIDTH * scale}px`,
                height: `${CARD_HEIGHT * scale}px`,
                backgroundImage: currentBackground ? `url(${currentBackground})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Grid overlay when no background */}
              {!currentBackground && (
                <div 
                  className="absolute inset-0 opacity-5"
                  style={{
                    backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
                    backgroundSize: `${10 * scale}px ${10 * scale}px`
                  }}
                />
              )}
              
              {/* Card border indicator */}
              <div className="absolute inset-0 border-2 border-dashed border-gray-300 pointer-events-none" />
              
              {/* Scaled content container */}
              <div 
                className="absolute inset-0"
                style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
              >
                {currentFields.filter(f => f.visible).map(field => (
                  <DraggableField
                    key={field.id}
                    field={field}
                    onUpdate={updateField}
                    onDelete={deleteField}
                    selected={selectedField === field.id}
                    onSelect={setSelectedField}
                    scale={scale}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Properties */}
        <div className="w-64 bg-white border-l overflow-y-auto">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">⚙️ Field Properties</h3>
          </div>
          <PropertiesPanel
            field={selectedFieldData}
            onUpdate={updateField}
          />
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between bg-purple-50">
              <div>
                <h3 className="text-lg font-semibold text-purple-900">👁️ I-Card Preview</h3>
                <p className="text-sm text-purple-600">View your I-Card with sample data</p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-purple-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6 bg-gray-100">
              <div className="flex gap-6 justify-center items-start">
                {/* Front Side Preview */}
                <div>
                  <p className="text-center text-sm font-medium text-gray-600 mb-2">Front Side</p>
                  <div 
                    className="relative bg-white shadow-xl"
                    style={{
                      width: `${CARD_WIDTH}px`,
                      height: `${CARD_HEIGHT}px`,
                      backgroundImage: template.front_background ? `url(${template.front_background})` : 'none',
                      backgroundSize: 'cover',
                      border: '1px solid #ddd'
                    }}
                  >
                    {template.front_fields.filter(f => f.visible).map(field => {
                      const isRound = field.shape === 'round';
                      const isMedia = ['photo', 'logo', 'qr_code', 'qr', 'signature'].includes(field.id);
                      return (
                        <div
                          key={field.id}
                          className={`absolute ${isMedia ? 'overflow-hidden' : ''}`}
                          style={{
                            left: `${field.x}px`,
                            top: `${field.y}px`,
                            width: `${field.width}px`,
                            height: isMedia ? `${field.height}px` : 'auto',
                            minHeight: `${field.height}px`,
                            fontSize: field.id === 'college_name' ? `${fitFontSize(template.college_name, field.fontSize, field.width)}px` : `${field.fontSize}px`,
                            color: field.color,
                            fontWeight: field.fontWeight || 'normal',
                            textAlign: field.textAlign || 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: field.textAlign === 'center' ? 'center' : field.textAlign === 'right' ? 'flex-end' : 'flex-start',
                            lineHeight: 1.15,
                            borderRadius: isRound ? '50%' : '0'
                          }}
                        >
                          {field.id === 'photo' && (
                            <div className={`w-full h-full bg-gray-200 flex items-center justify-center ${isRound ? 'rounded-full' : ''}`}>
                              <Users className="w-1/2 h-1/2 text-gray-400" />
                            </div>
                          )}
                          {field.id === 'logo' && (
                            template.college_logo ? (
                              <img src={template.college_logo} className={`w-full h-full object-cover ${isRound ? 'rounded-full' : ''}`} alt="" />
                            ) : (
                              <div className={`w-full h-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center ${isRound ? 'rounded-full' : ''}`}>
                                <span className="text-lg">🏛️</span>
                              </div>
                            )
                          )}
                          {field.id === 'college_name' && (template.college_name || 'College Name')}
                          {field.id === 'college_address' && (template.college_address || 'College Address')}
                          {field.id === 'college_contact' && (getContactInfo() || 'Email | Website | Phone')}
                          {field.id === 'name' && SAMPLE_STUDENT.name}
                          {field.id === 'course' && SAMPLE_STUDENT.course}
                          {field.id === 'student_no' && SAMPLE_STUDENT.student_no}
                          {field.id === 'session' && SAMPLE_STUDENT.session}
                          {field.id === 'admission_date' && SAMPLE_STUDENT.admission_date}
                          {field.id === 'blood_group' && SAMPLE_STUDENT.blood_group}
                          {field.id === 'phone' && SAMPLE_STUDENT.phone}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Back Side Preview */}
                <div>
                  <p className="text-center text-sm font-medium text-gray-600 mb-2">Back Side</p>
                  <div 
                    className="relative bg-white shadow-xl"
                    style={{
                      width: `${CARD_WIDTH}px`,
                      height: `${CARD_HEIGHT}px`,
                      backgroundImage: template.back_background ? `url(${template.back_background})` : 'none',
                      backgroundSize: 'cover',
                      border: '1px solid #ddd'
                    }}
                  >
                    {template.back_fields.filter(f => f.visible).map(field => (
                      <div
                        key={field.id}
                        className="absolute overflow-hidden"
                        style={{
                          left: `${field.x}px`,
                          top: `${field.y}px`,
                          width: `${field.width}px`,
                          height: `${field.height}px`,
                          fontSize: `${field.fontSize}px`,
                          color: field.color,
                          fontWeight: field.fontWeight || 'normal',
                          textAlign: field.textAlign || 'left',
                          display: 'flex',
                          alignItems: field.type === 'textarea' ? 'flex-start' : 'center',
                          justifyContent: field.textAlign === 'center' ? 'center' : 'flex-start',
                          lineHeight: field.type === 'textarea' ? '1.5' : 'normal'
                        }}
                      >
                        {field.id === 'terms_title' && (template.terms_title || 'TERMS & CONDITIONS')}
                        {field.id === 'terms' && (
                          <div className="whitespace-pre-wrap text-left w-full overflow-hidden">{template.terms_text || 'Terms and conditions will appear here...'}</div>
                        )}
                        {field.id === 'student_no_back' && SAMPLE_STUDENT.student_no}
                        {field.id === 'qr_code' && (
                          <img src={SAMPLE_QR} className="w-full h-full" alt="QR" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Legend */}
              <div className="mt-6 bg-white rounded-lg p-4 shadow">
                <h4 className="font-semibold text-gray-700 mb-2">📌 Sample Data Used:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-gray-500">Name:</span> {SAMPLE_STUDENT.name}</div>
                  <div><span className="text-gray-500">Course:</span> {SAMPLE_STUDENT.course}</div>
                  <div><span className="text-gray-500">Student No:</span> {SAMPLE_STUDENT.student_no}</div>
                  <div><span className="text-gray-500">Session:</span> {SAMPLE_STUDENT.session}</div>
                  <div><span className="text-gray-500">Admission:</span> {SAMPLE_STUDENT.admission_date}</div>
                  <div><span className="text-gray-500">Blood:</span> {SAMPLE_STUDENT.blood_group}</div>
                  <div><span className="text-gray-500">Phone:</span> {SAMPLE_STUDENT.phone}</div>
                </div>
                <p className="text-xs text-gray-400 mt-3">* Actual student data will be used during printing</p>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
              <Link
                to="/admission/print-icard"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Go to Print Page
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ICardBuilder;
